import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import H6 from '@base/H6/H6';
import Image from '@base/Image';
import P2 from '@base/P2/P2';
import { LMPSessionStatus } from '@constants/index';
import { getIsCancelledSession } from '@cont/OneToOneLiveMentorship/utils';
import styled from '@emotion/styled';
import { getFullName } from '@models/User';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import {
  addTime,
  getRelativeDayorCurrentDate,
  getTimeDifference,
  getTime
} from '@utils/dates';
import textHelper from '@utils/textHelper';
import SessionDetails from '@cont/OneToOneLiveMentorship/SessionDetails/Session';
import Button from '@base/Button/Button';
import { useEffect } from 'react';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import {
  MENTORSHIP_CARD_CLICKED,
  MENTORSHIP_SECTION_VIEWED
} from '@constants/segment';
import useLiveMentorship from '@api/hooks/activity/useLiveMentorship';
import useSessionStore from '@stores/live-mentorship/sessionStore';

const SectionWrapper = styled.div`
  position: relative;
  grid-column: span 18;
`;

const Card = styled.div`
  width: 368px;
  padding-top: ${Spacings.SPACING_4B};
  padding-bottom: ${Spacings.SPACING_4B};
  background: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  display: flex;
  margin-bottom: ${Spacings.SPACING_4B};
  margin-right: ${Spacings.SPACING_6B};
  justify-content: space-between;
  overflow: hidden;
  cursor: pointer;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const SessionDetailsWrapper = styled.div`
  width: calc(100% - 106px);
  margin-left: ${Spacings.SPACING_4B};
  display: flex;
  flex-direction: column;
  align-items: space-between;
`;

const Title = styled(H5)`
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  margin-bottom: ${Spacings.SPACING_2B};
  font-weight: ${Fonts.SEMIBOLD};
  color: ${({ $greyText }) =>
    $greyText ? 'var(--color-text-secondary)' : 'var(--color-text-primary)'};
  text-decoration-line: ${({ $isCancelled }) =>
    $isCancelled ? 'line-through' : 'none'};
`;

const Subtitle = styled(H6)`
  color: ${({ $greyText }) =>
    $greyText ? 'var(--color-text-secondary)' : ' var(--color-i-blue-18);'};
  margin-bottom: ${Spacings.SPACING_3B};
`;

const SessionText = styled(P2)`
  color: var(--color-text-secondary);
`;

const CardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${Spacings.SPACING_8B};
`;

const StyH4 = styled(H4)`
  text-transform: capitalize;
  color: var(--color-text-primary);
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 98px;
  width: 84px;
  margin-top: ${Spacings.SPACING_8B};
`;

const Circle = styled.div`
  display: ${({ $hide }) => ($hide ? 'none' : 'block')};
  width: 12px;
  height: 12px;
  border-radius: 50%;
  position: absolute;
  left: 2px;
  top: 38px;
  background: ${({ $greyText }) =>
    $greyText ? 'var(--color-i-gray-25)' : ' var(--color-i-blue-18);'};
  z-index: 11;
`;

const HalfCircle = styled.div`
  display: ${({ $hide }) => ($hide ? 'none' : 'block')};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  position: absolute;
  right: -9px;
  top: -4px;
  background: ${({ $greyText }) =>
    $greyText ? 'var(--color-i-gray-25)' : ' var(--color-i-blue-18);'};
`;

const CardBottomWrapper = styled.div`
  margin-top: auto;
`;

const StyledImage = styled(Image)`
  z-index: 10;
  border-radius: 50%;
  margin-right: -${Spacings.SPACING_3B} !important;
`;

const FlexWrap = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacings.SPACING_3B};
`;

const LiveTag = styled.div`
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.HEAVY};
  border-radius: ${Spacings.SPACING_1B};
  background: var(--color-i-red);
  width: max-content;
  padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_2B};
  color: white;
  margin-right: ${Spacings.SPACING_2B};
`;

const getEducatorIcon = (status, educator) => {
  const isMentorAssigned = !!educator;
  const educatorAvatar = educator?.avatar;
  if (status === LMPSessionStatus.UPCOMING && isMentorAssigned) {
    return educatorAvatar || 'lmp/mentor-placeholder-gray.svg';
  }
  if (status === LMPSessionStatus.UPCOMING && !isMentorAssigned) {
    return 'lmp/mentor-placeholder.svg';
  }
  if (getIsCancelledSession(status)) {
    return educatorAvatar || 'lmp/mentor-placeholder-gray.svg';
  }
  return educatorAvatar || 'lmp/mentor-placeholder.svg';
};

const getSessionEventProps = (session, isLive) => {
  return {
    is_live: isLive,
    session_uid: session.uid,
    mentor_uid: session?.educator.uid,
    mentor_username: session?.educator.username,
    start_time: session?.slot.startTime,
    session_duration: session?.slot.duration,
    session_state: isLive ? 'live' : 'upcoming'
  };
};

const TWO_MINS = 2;

const LiveMentorshipCard = ({ session, goalUID }) => {
  const {
    status,
    vertical = {},
    slot = {},
    isRefunded,
    uid: sessionUID,
    isWelcomeSession,
    educator
  } = session;

  const goalEventProps = useGoalEventProps({ goalUID });
  const { setSessionDrawerUID } = useSessionStore();
  const { startTime } = slot;
  const isCancelled = getIsCancelledSession(status);
  const level10Ver = vertical.parent?.title;
  const level5Ver = vertical.parent?.parent?.title;
  const isMentorAssigned = !!educator;
  const title = isWelcomeSession
    ? 'Welcome session'
    : `${level5Ver ? `${level5Ver} •` : ''} ${level10Ver || ''}`;
  const dateString = getRelativeDayorCurrentDate(startTime);
  const timeDiff = getTimeDifference(null, startTime, true, false, true);
  const sessionEndTime = addTime(new Date(startTime), slot?.duration, 'm');
  const isLive =
    timeDiff < TWO_MINS &&
    status <= LMPSessionStatus.STARTED &&
    new Date() < sessionEndTime;
  const eduIcon = getEducatorIcon(status, educator, isLive, isRefunded);
  const timeString = !isCancelled ? ` ${getTime(startTime)}` : '';
  const isUpcoming = status === LMPSessionStatus.UPCOMING;
  const isCompleted = !isCancelled && !isRefunded && !isUpcoming && !isLive;
  let educatorText = educator
    ? getFullName(educator.firstName, educator.lastName)
    : 'Mentor will be assigned';

  if (!isMentorAssigned && isCancelled) {
    educatorText = 'Mentor not assigned';
  }

  let subText = '';

  if (isUpcoming && isMentorAssigned) {
    subText = `Starts at ${
      dateString === 'Today' ? '' : dateString
    }${timeString} • ${textHelper.pluralize(slot?.duration, 'min')}`;
  }

  if (isLive) {
    subText = `Started at ${
      dateString === 'Today' ? '' : dateString
    }${timeString}`;
  }

  if (isUpcoming && !isMentorAssigned) {
    subText = textHelper.pluralize(slot?.duration, 'min');
  }

  if (isCancelled) {
    subText = 'Cancelled';
  }

  if (isCompleted) {
    subText = 'Completed';
  }

  const hideCircles =
    (isCancelled && !isMentorAssigned) ||
    (isUpcoming && !educator?.avatar && !(isUpcoming && !isMentorAssigned));
  const greyText = isCancelled || isCompleted;

  const onSessionClick = () => {
    setSessionDrawerUID(sessionUID);
    const sessionEventProps = getSessionEventProps(session, isLive);
    segment.track(MENTORSHIP_CARD_CLICKED, {
      ...goalEventProps,
      ...sessionEventProps
    });
  };

  return (
    <Card onClick={onSessionClick}>
      <SessionDetailsWrapper>
        <>
          <Subtitle $greyText={greyText}>MENTORSHIP</Subtitle>
          <Title $greyText={greyText} $isCancelled={isCancelled}>
            {title}
          </Title>
          <FlexWrap>
            {isLive && <LiveTag>LIVE</LiveTag>}
            <SessionText>{subText}</SessionText>
          </FlexWrap>
          <CardBottomWrapper>
            {isLive ? (
              <Button label="Join" maxHeight="xs" color="black" width="120px" />
            ) : (
              <P2
                color={
                  greyText
                    ? 'var(--color-text-secondary)'
                    : ' var(--color-text-primary);'
                }
              >
                {educatorText}
              </P2>
            )}
          </CardBottomWrapper>
        </>
      </SessionDetailsWrapper>
      <ImageWrapper>
        <Circle $hide={hideCircles} $greyText={greyText} />
        <StyledImage src={eduIcon} alt="mentor" height={98} width={98} />
        <HalfCircle $hide={hideCircles} $greyText={greyText} />
      </ImageWrapper>
    </Card>
  );
};

const LiveMentorshipFeedBlock = ({ feedBlock, goalUID }) => {
  const { extraBlockInfo } = feedBlock;
  const segmentGoalProps = useGoalEventProps({ goalUID });

  const { data } = useLiveMentorship(goalUID);

  useEffect(() => {
    segment.track(MENTORSHIP_SECTION_VIEWED, { ...segmentGoalProps });
  }, [segmentGoalProps]);

  if (data.length < 1) return null;

  return (
    <SectionWrapper>
      <StyH4>{extraBlockInfo?.header || 'Today’s Mentorship Session'}</StyH4>
      <CardsWrapper>
        {data.slice(0, 2).map((session) => (
          <LiveMentorshipCard
            session={session}
            goalUID={goalUID}
            key={session.uid}
          />
        ))}
      </CardsWrapper>
      <SessionDetails goalUID={goalUID} />
    </SectionWrapper>
  );
};

export default LiveMentorshipFeedBlock;
